// src/features/analytics/useAnalyticsMediaPeaks.ts

import { useQuery } from '@tanstack/react-query';
import { AnalyticsApi } from './AnalyticsApi';
import { ANALYTICS_QUERY_KEYS } from '@/lib/constants/analytics';
import { FEATURE_FLAGS } from '@/lib/constants/featureFlags';
import type { UtcRange } from '@/shared/utils/utcRange';
import type { AnalyticsEnvelope, MediaPeaksData } from './types';

export interface UseAnalyticsMediaPeaksResult {
  peaks: AnalyticsEnvelope<MediaPeaksData> | null;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  refetch: () => void;
}

/**
 * Per-media daily peak concurrency for the Content-tab Drawer (metrics
 * expansion) — account scope only, ranged one-shot (never polled). Pass null
 * cid or mediaId to disable; the build-time expansion flag hard-disables the
 * query while the feature ships dark.
 */
export function useAnalyticsMediaPeaks(
  cid: number | null,
  mediaId: string | null,
  range: UtcRange
): UseAnalyticsMediaPeaksResult {
  const query = useQuery({
    queryKey: ANALYTICS_QUERY_KEYS.mediaPeaks(cid ?? 0, mediaId ?? '', range.from, range.to),
    queryFn: () => AnalyticsApi.getMediaPeaks(cid!, mediaId!, range),
    enabled: FEATURE_FLAGS.analyticsExpansionEnabled && cid !== null && mediaId !== null,
    staleTime: 60_000,
  });

  return {
    peaks: query.data ?? null,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  };
}
